/**
 * Pure extractors that surface nested property attributes for the detail page.
 * Keeps display keys aligned with the saved OpenAPI / PropertiesModel shape.
 */

import type {
  PropertyCondominium,
  PropertyDocumentation,
  PropertyLocation,
  PropertyMedia,
  PropertyMetadata,
  PropertyOwnership,
  RentalData,
  SaleData,
} from '@services/housien/types-property';

export type DetailFieldRow = {
  labelKey: string;
  value: string;
};

/**
 * Pushes a row when value is a non-empty string or number.
 */
function pushDefined(
  rows: DetailFieldRow[],
  labelKey: string,
  value: string | number | undefined | null,
): void {
  if (value === undefined || value === null || value === '') return;
  rows.push({ labelKey, value: String(value) });
}

/**
 * Collects registry documentation metadata rows.
 */
export function collectDocumentationRows(
  documentation: PropertyDocumentation | undefined,
): DetailFieldRow[] {
  const rows: DetailFieldRow[] = [];
  pushDefined(rows, 'detailView.documents.registrationNumber', documentation?.registration_number);
  pushDefined(rows, 'detailView.documents.registryOffice', documentation?.registry_office);
  pushDefined(rows, 'detailView.documents.inscriptionNumber', documentation?.inscription_number);
  return rows;
}

/**
 * Collects rental commercial-condition rows.
 */
export function collectRentalConditionRows(
  rental: RentalData | undefined,
): DetailFieldRow[] {
  const rows: DetailFieldRow[] = [];
  pushDefined(rows, 'detailView.terms.leaseTerm', rental?.min_contract_months);
  pushDefined(rows, 'detailView.terms.guarantee', rental?.guarantee_type);
  pushDefined(rows, 'detailView.terms.paymentFrequency', rental?.payment_frequency);
  if (rental?.included_expenses?.length) {
    rows.push({
      labelKey: 'detailView.terms.includedExpenses',
      value: rental.included_expenses.join(', '),
    });
  }
  pushDefined(rows, 'detailView.terms.administration', rental?.administration);
  return rows;
}

/**
 * Collects sale condition rows (exchange accepted).
 */
export function collectSaleConditionRows(sale: SaleData | undefined): DetailFieldRow[] {
  if (!sale?.accepts_exchange) return [];
  return [{ labelKey: 'detailView.terms.acceptsExchange', value: 'yes' }];
}

/**
 * Collects ownership and mandate rows.
 */
export function collectOwnershipRows(
  ownership: PropertyOwnership | undefined,
): DetailFieldRow[] {
  const rows: DetailFieldRow[] = [];
  pushDefined(rows, 'detailView.ownership.owner', ownership?.owner_client_id);
  const mandate = ownership?.mandate;
  pushDefined(rows, 'detailView.ownership.mandateType', mandate?.mandate_type);
  pushDefined(rows, 'detailView.ownership.mandateStart', mandate?.start_date);
  pushDefined(rows, 'detailView.ownership.mandateEnd', mandate?.end_date);
  if (mandate?.commission_percentage != null) {
    rows.push({
      labelKey: 'detailView.ownership.commission',
      value: `${mandate.commission_percentage}%`,
    });
  }
  return rows;
}

/**
 * Resolves a stored owner client id to a human-readable label.
 */
export function resolveOwnerDisplayName(
  ownerId: string | undefined,
  clients: Array<{ id: string; label: string }>,
  loading = false,
): string | undefined {
  if (!ownerId) return undefined;
  const match = clients.find((c) => c.id === ownerId);
  if (match?.label) return match.label;
  if (loading) return '…';
  return ownerId;
}

/**
 * Collects media links such as virtual tour URL.
 */
export function collectMediaLinkRows(media: PropertyMedia | undefined): DetailFieldRow[] {
  const rows: DetailFieldRow[] = [];
  pushDefined(rows, 'detailView.media.virtualTour', media?.virtual_tour_url);
  return rows;
}

/**
 * Returns metadata tags when present.
 */
export function collectTagValues(metadata: PropertyMetadata | undefined): string[] {
  return metadata?.tags?.length ? [...metadata.tags] : [];
}

/**
 * Returns condominium included expenses when present.
 */
export function collectCondoExpenseValues(
  condominium: PropertyCondominium | undefined,
): string[] {
  return condominium?.included_expenses?.length
    ? [...condominium.included_expenses]
    : [];
}

/**
 * Formats street, number and complement for location display.
 */
export function formatStreetWithComplement(
  location: PropertyLocation | undefined,
): string | undefined {
  if (!location?.street) return undefined;
  const base = location.number ? `${location.street}, ${location.number}` : location.street;
  return location.complement ? `${base}, ${location.complement}` : base;
}
